import { Fragment, useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import type { TenantInfo } from '../api/types.js';
import { useApi } from '../auth.js';
import { ErrorBox, Spinner } from '../components.js';
import { ConfirmDialog } from '../EdgeLifecycle.js';
import { useResource } from '../hooks.js';
import { sitesWithTenant, slugify, TENANT_SLUG } from '../lib/tenants.js';
import { roleSummary, sortUsers, userErrorText } from '../lib/users.js';

function CreateTenantForm({ onCreated }: { onCreated: () => void }) {
  const api = useApi();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const effectiveSlug = slugTouched ? slug : slugify(name);

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createTenant(name.trim(), effectiveSlug);
      setName('');
      setSlug('');
      setSlugTouched(false);
      onCreated();
    } catch (err) {
      setError(userErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="card token-form"
      onSubmit={(e) => {
        void submit(e);
      }}
    >
      <div className="edge-form-row">
        <label className="field">
          <span>Name</span>
          <input
            type="text"
            required
            maxLength={200}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span>Slug</span>
          <input
            type="text"
            className="mono"
            required
            maxLength={63}
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
          />
        </label>
      </div>
      {effectiveSlug && !TENANT_SLUG.test(effectiveSlug) && (
        <p className="form-error">Lower-case letters, digits and dashes; starts with a letter.</p>
      )}
      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}
      <div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy || !name.trim() || !TENANT_SLUG.test(effectiveSlug)}
        >
          {busy ? 'Creating…' : 'Create tenant'}
        </button>
      </div>
    </form>
  );
}

function MoveSiteForm({ tenants, onMoved }: { tenants: TenantInfo[]; onMoved: () => void }) {
  const api = useApi();
  const sites = sitesWithTenant(tenants);
  const [siteId, setSiteId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const site = sites.find((s) => s.id === siteId);
  const target = tenants.find((t) => t.id === tenantId);

  async function move() {
    setBusy(true);
    setError(null);
    try {
      await api.moveSite(siteId, tenantId);
      setConfirming(false);
      setSiteId('');
      setTenantId('');
      onMoved();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card token-form">
      <div className="edge-form-row">
        <label className="field">
          <span>Site</span>
          <select
            value={siteId}
            onChange={(e) => {
              setSiteId(e.target.value);
            }}
          >
            <option value="">Choose a site…</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.tenantName})
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>To tenant</span>
          <select
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
            }}
          >
            <option value="">Choose a tenant…</option>
            {tenants
              .filter((t) => t.id !== site?.tenantId)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </label>
      </div>
      <div>
        <button
          type="button"
          className="btn"
          disabled={!site || !target || target.id === site.tenantId}
          onClick={() => {
            setConfirming(true);
          }}
        >
          Move site
        </button>
      </div>
      {confirming && site && target && (
        <ConfirmDialog
          title="Move site"
          confirmLabel="Move"
          danger
          busy={busy}
          error={error}
          onCancel={() => {
            setConfirming(false);
            setError(null);
          }}
          onConfirm={() => {
            void move();
          }}
        >
          <p>
            Move <strong>{site.name}</strong> from {site.tenantName} to{' '}
            <strong>{target.name}</strong>? Its rules and rule alarms move with it. Users and API
            tokens of {site.tenantName} lose access to it at once.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function TenantUsers({ tenantId }: { tenantId: string }) {
  const api = useApi();
  const users = useResource(`tenant-users:${tenantId}`, (s) => api.users(s, tenantId));
  if (users.error) return <ErrorBox error={users.error} onRetry={users.reload} />;
  if (!users.data) return <Spinner />;
  if (users.data.length === 0) {
    return <p className="muted small">No users yet. Add them under Users.</p>;
  }
  return (
    <ul>
      {sortUsers(users.data).map((u) => (
        <li key={u.id} className={u.disabled ? 'muted' : undefined}>
          {u.email}{' '}
          <span className="muted small">
            {roleSummary({ ...u, tenants: u.tenants.filter((t) => t.tenant_id === tenantId) }).join(
              ', ',
            )}
            {u.disabled ? ' · disabled' : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Tenants (ADR 0010, platform staff): customers with their sites and users. */
export function TenantsPage() {
  const api = useApi();
  const tenants = useResource('tenants', (s) => api.tenants(s));
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section>
      <h1>Tenants</h1>
      <p className="muted">
        Customers and their sites. Tenant users see only the sites of their tenant; give them roles
        under <Link to="/settings/users">Users</Link>.
      </p>
      {tenants.error && <ErrorBox error={tenants.error} onRetry={tenants.reload} />}
      {!tenants.data ? (
        !tenants.error && <Spinner />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Slug</th>
                <th>Sites</th>
                <th className="num">Users</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {tenants.data.map((t) => (
                <Fragment key={t.id}>
                  <tr>
                    <td className="strong">{t.name}</td>
                    <td className="mono small">{t.slug}</td>
                    <td>
                      {t.sites.length === 0 ? (
                        <span className="muted">none</span>
                      ) : (
                        t.sites.map((s, i) => (
                          <span key={s.id}>
                            {i > 0 && ', '}
                            <Link to={`/sites/${encodeURIComponent(s.id)}`}>{s.name}</Link>
                          </span>
                        ))
                      )}
                    </td>
                    <td className="num">{t.users}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-small"
                        aria-expanded={open === t.id}
                        onClick={() => {
                          setOpen(open === t.id ? null : t.id);
                        }}
                      >
                        {open === t.id ? 'Hide users' : 'Users'}
                      </button>
                    </td>
                  </tr>
                  {open === t.id && (
                    <tr>
                      <td colSpan={5}>
                        <TenantUsers tenantId={t.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="section-head">
        <h2>Create tenant</h2>
      </div>
      <CreateTenantForm onCreated={tenants.reload} />
      {tenants.data && tenants.data.length > 1 && (
        <>
          <div className="section-head">
            <h2>Move a site</h2>
          </div>
          <MoveSiteForm tenants={tenants.data} onMoved={tenants.reload} />
        </>
      )}
    </section>
  );
}
